
import { useEffect, useState } from 'react';
import { HealthScore } from '@/types/healthScore';

interface HealthScoreGaugeProps {
  score: number;
  baselineScore?: number;
  actionableScore?: number;
  maxScore?: number;
  size?: 'sm' | 'md' | 'lg';
  healthScore?: HealthScore | null;
}

export const HealthScoreGauge = ({ 
  score, 
  baselineScore = 180, 
  actionableScore = 562, 
  maxScore = 1000, 
  size = 'lg' 
}: HealthScoreGaugeProps) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [animatedBaseline, setAnimatedBaseline] = useState(0);
  const [animatedActionable, setAnimatedActionable] = useState(0);
  
  const radius = size === 'sm' ? 50 : size === 'md' ? 70 : 90;
  const strokeWidth = size === 'sm' ? 8 : size === 'md' ? 10 : 12;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  
  // Calculate percentage and determine color
  const percentage = Math.min(score / maxScore, 1);
  const strokeDashoffset = circumference - percentage * circumference;
  
  const getScoreColor = (score: number) => {
    if (score >= 850) return '#22C55E'; // green-500 - A grade (excellent)
    if (score >= 700) return '#84CC16'; // lime-500 - B grade (good)
    if (score >= 550) return '#EAB308'; // yellow-500 - C grade (fair)
    if (score >= 400) return '#F97316'; // orange-500 - D grade (needs attention)
    return '#EF4444'; // red-500 - E grade (poor)
  };

  const getScoreGrade = (score: number) => {
    if (score >= 850) return 'A';
    if (score >= 700) return 'B';
    if (score >= 550) return 'C';
    if (score >= 400) return 'D';
    return 'E';
  };

  const getGradeLabel = (grade: string) => {
    switch (grade) {
      case 'A': return 'Excellent';
      case 'B': return 'Good';
      case 'C': return 'Fair';
      case 'D': return 'Needs Attention';
      case 'E': return 'Poor';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const end = score;
      const duration = 800;
      const increment = end / (duration / 16);
      
      const animate = () => {
        start += increment;
        if (start < end) {
          setAnimatedScore(Math.floor(start));
          setAnimatedBaseline(Math.floor((start / end) * baselineScore));
          setAnimatedActionable(Math.floor((start / end) * actionableScore));
          requestAnimationFrame(animate);
        } else {
          setAnimatedScore(end);
          setAnimatedBaseline(baselineScore);
          setAnimatedActionable(actionableScore);
        }
      };
      animate();
    }, 200);

    return () => clearTimeout(timer);
  }, [score, baselineScore, actionableScore]);

  const svgSize = radius * 2;
  const textSize = size === 'sm' ? 'text-lg' : size === 'md' ? 'text-2xl' : 'text-4xl';
  const labelSize = size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base';
  const grade = getScoreGrade(animatedScore);

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <svg
          height={svgSize}
          width={svgSize}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            stroke="#e5e7eb"
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          {/* Progress circle */}
          <circle
            stroke={getScoreColor(animatedScore)}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            className="transition-all duration-700 ease-out"
            style={{
              filter: 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.1))'
            }}
          />
        </svg>
        
        {/* Score display in center */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={`font-bold ${textSize}`} style={{ color: getScoreColor(animatedScore) }}>
            {animatedScore}
          </div>
          <div className={`text-muted-foreground ${labelSize} font-medium`}>
            Grade {grade}
          </div>
        </div>
      </div>

      {/* Baseline/Actionable Breakdown Bar */}
      <div className="w-full max-w-xs">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>Baseline: {animatedBaseline}</span>
          <span>Actionable: {animatedActionable}</span>
        </div>
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
          <div 
            className="bg-blue-500 transition-all duration-700 ease-out"
            style={{ width: `${(animatedBaseline / 1000) * 100}%` }}
          />
          <div 
            className="bg-primary transition-all duration-700 ease-out"
            style={{ width: `${(animatedActionable / 1000) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span className="text-blue-600">Baseline (250 max)</span>
          <span className="text-primary">Actionable (750 max)</span>
        </div>
      </div>
    </div>
  );
};
